/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import * as _ from 'lodash';
import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/solid-gauge';

import template from './speedo.component.html';

@Component({
  standalone: false,
  selector: 'adn-speedo',
  template
})
export class SpeedoComponent implements OnChanges, OnDestroy {
  @Input() stat: number;
  @Input() high: number;
  @Input() low: number;

  haveStats = false;
  chartHeight = 70;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;

  ngOnChanges(): void {
    this.updateChartState();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private updateChartState(): void {
    if (this.stat === null || this.stat === undefined) {
      this.haveStats = false;
      this.chartOptions = null;
      this.destroyChart();
      return;
    }

    this.chartOptions = _.merge(this.createBaseOptions(), {
      yAxis: {
        min: this.low || 0,
        max: this.high || 100
      },
      series: [{
        name: 'Speed',
        data: [Math.round((this.stat + Number.EPSILON) * 100) / 100],
        dataLabels: {
          format:
            '<div style="text-align:center">' +
            '<span style="font-size:12px">{y}%</span>' +
            '</div>'
        },
        tooltip: {
          valueSuffix: '%'
        }
      }]
    });
    this.haveStats = true;
    this.renderChart();
  }

  private createBaseOptions(): any {
    return {
      chart: {
        type: 'solidgauge',
        height: this.chartHeight
      },
      title: null,
      pane: {
        size: '210%',
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '110%'],
        background: {
          backgroundColor: '#ddd',
          innerRadius: '60%',
          outerRadius: '100%',
          shape: 'arc'
        }
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      tooltip: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      yAxis: {
        stops: [
          [0.2, '#f48024'],
          [0.75, '#55BF3B']
        ],
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: null,
        tickAmount: 2,
        title: {
          enabled: false
        },
        labels: {
          enabled: false
        }
      },
      plotOptions: {
        solidgauge: {
          dataLabels: {
            y: 10,
            borderWidth: 0,
            useHTML: true
          }
        }
      }
    };
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
